export interface PortfolioUxCaseStudy {
  sectionTitle: string;
  sectionDescription: string;
  blocks?: Array<{ title: string; body: string }>;
}

export interface PortfolioImage {
  id: string;
  title: string;
  description: string;
  description_long?: string;
  image_url: string;
  image_url_2x?: string;
  url?: string;
  target?: string;
  rel?: string;
  hasOverlay?: boolean;
  hasPageSlug?: boolean;
  pageSlug?: string;
  /** When true, the item stays in locale data but is not listed or routable. */
  hidden?: boolean;
  uxCaseStudy?: PortfolioUxCaseStudy;
}

export interface PortfolioSection {
  title: string;
  description?: string;
  portfolio_url?: string;
  portfolio_images?: PortfolioImage[];
}

interface PortfolioRootSection {
  portfolio_sections?: PortfolioSection[];
}

interface PortfolioContent {
  site_title?: string;
  site_description?: string;
  keywords?: string[];
  sections?: PortfolioRootSection[];
}

function normalizeLocale(locale?: string): 'en' | 'es' {
  return locale === 'es' ? 'es' : 'en';
}

export async function loadPortfolioContent(locale?: string): Promise<PortfolioContent> {
  const lang = normalizeLocale(locale);
  const data = await import(`@/locales/${lang}/portfolio.json`);
  return (data?.default ?? data) as PortfolioContent;
}

export function getPortfolioSections(content: PortfolioContent): PortfolioSection[] {
  const root = Array.isArray(content.sections)
    ? content.sections.find((s) => Array.isArray(s?.portfolio_sections))
    : undefined;
  return root?.portfolio_sections ?? [];
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function sectionSlug(section: PortfolioSection): string {
  const url = (section.portfolio_url ?? '').trim();
  if (url) {
    const cleaned = url.replace(/\/+$/, '');
    const parts = cleaned.split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    if (last) return last;
  }
  return slugify(section.title || 'section');
}

export function getPortfolioSectionBySlug(
  sections: PortfolioSection[],
  slug: string
): PortfolioSection | undefined {
  return sections.find((s) => sectionSlug(s) === slug);
}

export function getPortfolioImageBySectionAndSlug(
  sections: PortfolioSection[],
  sectionSlugParam: string,
  itemSlug: string
): { section: PortfolioSection; image: PortfolioImage } | undefined {
  const section = getPortfolioSectionBySlug(sections, sectionSlugParam);
  if (!section?.portfolio_images?.length) return undefined;
  const image = section.portfolio_images.find(
    (img) => img.hidden !== true && (img.pageSlug ?? '').trim() === itemSlug
  );
  if (!image) return undefined;
  return { section, image };
}

export function getPortfolioItemStaticParams(
  content: PortfolioContent
): Array<{ section: string; slug: string }> {
  const sections = getPortfolioSections(content);
  const out: Array<{ section: string; slug: string }> = [];
  for (const s of sections) {
    const sec = sectionSlug(s);
    for (const img of s.portfolio_images ?? []) {
      if (img.hidden === true) continue;
      const slug = (img.pageSlug ?? '').trim();
      if (slug) out.push({ section: sec, slug });
    }
  }
  return out;
}
